<template>
    <div class="q-mt-md q-gutter-y-md">
        <q-card class="q-pb-lg no-shadow border-grey">
            <q-tabs
                v-model="activeTab"
                class="primary bg-grey-3"
                active-color="primary"
                indicator-color="primary"
                align="justify"
            >
                <q-tab
                    id="detail-tab"
                    :label="$t('Detail')"
                    name="detail"
                    icon="fas fa-edit"
                />
                <q-tab
                    id="causes-tab"
                    :label="$t('Causes')"
                    name="causes"
                    icon="fas fa-exclamation-square"
                />
                <q-tab
                    id="products-tab"
                    :label="$t('Products')"
                    name="products"
                    icon="fas fa-box-full"
                />
                <q-tab
                    v-if="showQualityDefectsTab"
                    id="quality-tab"
                    :label="$t('Quality Defects')"
                    name="quality"
                    icon="fa-solid fa-clipboard-list-check"
                />
                <q-tab
                    id="notification-tab"
                    v-if="$userCan('edit', 'recipients')"
                    :label="$t('Email Notifications')"
                    name="extended_downtime_recipients"
                    icon="fas fa-paper-plane"
                />
            </q-tabs>

            <q-separator></q-separator>

            <q-tab-panels v-model="activeTab" animated keep-alive>
                <q-tab-panel name="detail" class="tab-panel">
                    <asset-detail :asset="asset" :facilityId="facilityId" />
                </q-tab-panel>

                <q-tab-panel name="causes" class="tab-panel">
                    <asset-causes :asset="asset" :facilityId="facilityId" />
                </q-tab-panel>

                <q-tab-panel name="products" class="tab-panel">
                    <asset-products :asset="asset" :facilityId="facilityId" />
                </q-tab-panel>

                <q-tab-panel name="quality" class="tab-panel">
                    <asset-quality-defect-list
                        :asset="asset"
                        :facilityId="facilityId"
                    />
                </q-tab-panel>

                <q-tab-panel
                    name="extended_downtime_recipients"
                    class="tab-panel"
                >
                    <asset-recipients :asset="asset" :facilityId="facilityId" />
                </q-tab-panel>
            </q-tab-panels>
        </q-card>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import AssetCauses from './AssetCauseList.vue';
import AssetDetail from './AssetDetail.vue';
import AssetProducts from './AssetProductList.vue';
import AssetQualityDefectList from './AssetQualityDefectList.vue';
import AssetRecipients from './AssetRecipientList.vue';
import { mapState, mapActions } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useFiltersStore } from '@stores/filters.js';
import { useAssetsStore } from '@stores/assets';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'AssetSettingTabs',
    components: {
        AssetDetail,
        AssetCauses,
        AssetProducts,
        AssetRecipients,
        AssetQualityDefectList
    },
    mixins: [BreadcrumbsManager],
    props: {
        assetId: {
            type: [String, Number],
            required: true
        },
        facilityId: {
            type: [String, Number],
            required: true
        }
    },
    created() {
        // get asset details from the store
        this.asset = this.getAsset(this.assetId);
        // check if there is storedActive tab in
        // the state, if it is there then set the
        // active tab otherwise default to detail
        // page.
        const storedActiveTab = this.getStoredAssetFilter({
            facilityId: this.facilityId,
            assetId: this.assetId,
            filter: 'tabSettings'
        });
        if (storedActiveTab) {
            this.activeTab = storedActiveTab;
        } else {
            this.activeTab = 'detail';
        }
    },
    mounted() {
        // set breadcrumbs
        const facility = this.getFacility(this.facilityId);
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: facility?.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: facility?.id
                    }
                }
            },
            {
                label: this.asset?.name,
                route: {
                    name: 'asset_dashboard',
                    params: {
                        assetId: this.assetId
                    }
                }
            },
            {
                label: this.$t('Asset Settings')
            }
        ]);
    },
    watch: {
        // watch the activeTab property of
        // data object, if it changes, propagate
        // the changes to the state as well.
        activeTab: {
            handler() {
                this.setAssetFilter({
                    facilityId: this.facilityId,
                    assetId: this.assetId,
                    filter: 'tabSettings',
                    value: this.activeTab
                });
            }
        }
    },
    data() {
        return {
            activeTab: 'detail',
            asset: {}
        };
    },
    computed: {
        ...mapState(useAssetsStore, {
            getAsset: 'getAsset'
        }),
        ...mapState(useFacilitiesStore, {
            getFacility: 'getFacility'
        }),
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useFiltersStore, ['getStoredAssetFilter']),
        /**
         * Determine if the quality tab should be visible.
         * @returns {boolean} - True if the quality tab should be visible, false otherwise.
         */
        showQualityDefectsTab() {
            return (
                this.isTrackingQualityEvents &&
                this.asset?.enable_quality_event_recording
            );
        }
    },
    methods: {
        ...mapActions(useFiltersStore, ['setAssetFilter'])
    }
};
</script>

<style lang="scss" scoped>
.tab-panel {
    height: 75vh;
}
</style>
